import { Task, ProjectInfo } from '../types';

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  category: 'software' | 'marketing' | 'operations' | 'strategy' | 'general';
  tasks: Omit<Task, 'id'>[];
  projectInfo: Omit<ProjectInfo, 'date'>;
  matrixType: 'RAPID' | 'RASCI';
}

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'software-development',
    name: 'Software Development Project',
    description: 'Template for software development initiatives with common roles and tasks',
    category: 'software',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'Software Development Project - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Requirements',
        taskDescription: 'Define functional requirements',
        assignments: {}
      },
      {
        workArea: 'Requirements',
        taskDescription: 'Define technical specifications',
        assignments: {}
      },
      {
        workArea: 'Architecture',
        taskDescription: 'Design system architecture',
        assignments: {}
      },
      {
        workArea: 'Development',
        taskDescription: 'Implement core features',
        assignments: {}
      },
      {
        workArea: 'Testing',
        taskDescription: 'Conduct system testing',
        assignments: {}
      },
      {
        workArea: 'Deployment',
        taskDescription: 'Deploy to production',
        assignments: {}
      }
    ]
  },
  {
    id: 'marketing-campaign',
    name: 'Marketing Campaign',
    description: 'Template for marketing campaign planning and execution',
    category: 'marketing',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'Marketing Campaign - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Strategy',
        taskDescription: 'Define campaign objectives',
        assignments: {}
      },
      {
        workArea: 'Strategy',
        taskDescription: 'Identify target audience',
        assignments: {}
      },
      {
        workArea: 'Creative',
        taskDescription: 'Develop creative concepts',
        assignments: {}
      },
      {
        workArea: 'Creative',
        taskDescription: 'Create marketing materials',
        assignments: {}
      },
      {
        workArea: 'Execution',
        taskDescription: 'Launch campaign',
        assignments: {}
      },
      {
        workArea: 'Analysis',
        taskDescription: 'Measure campaign performance',
        assignments: {}
      }
    ]
  },
  {
    id: 'product-launch',
    name: 'Product Launch',
    description: 'Template for new product launch initiatives',
    category: 'strategy',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'Product Launch - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Planning',
        taskDescription: 'Define launch strategy',
        assignments: {}
      },
      {
        workArea: 'Planning',
        taskDescription: 'Set launch timeline',
        assignments: {}
      },
      {
        workArea: 'Marketing',
        taskDescription: 'Develop go-to-market plan',
        assignments: {}
      },
      {
        workArea: 'Operations',
        taskDescription: 'Prepare supply chain',
        assignments: {}
      },
      {
        workArea: 'Sales',
        taskDescription: 'Train sales team',
        assignments: {}
      },
      {
        workArea: 'Launch',
        taskDescription: 'Execute product launch',
        assignments: {}
      }
    ]
  },
  {
    id: 'process-improvement',
    name: 'Process Improvement',
    description: 'Template for operational process improvement projects',
    category: 'operations',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'Process Improvement - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Analysis',
        taskDescription: 'Analyze current process',
        assignments: {}
      },
      {
        workArea: 'Analysis',
        taskDescription: 'Identify improvement opportunities',
        assignments: {}
      },
      {
        workArea: 'Design',
        taskDescription: 'Design improved process',
        assignments: {}
      },
      {
        workArea: 'Testing',
        taskDescription: 'Pilot new process',
        assignments: {}
      },
      {
        workArea: 'Implementation',
        taskDescription: 'Roll out new process',
        assignments: {}
      },
      {
        workArea: 'Monitoring',
        taskDescription: 'Monitor process performance',
        assignments: {}
      }
    ]
  },
  {
    id: 'general-project',
    name: 'General Project',
    description: 'Basic template for any type of project',
    category: 'general',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'General Project - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Initiation',
        taskDescription: 'Define project scope',
        assignments: {}
      },
      {
        workArea: 'Planning',
        taskDescription: 'Create project plan',
        assignments: {}
      },
      {
        workArea: 'Execution',
        taskDescription: 'Execute project tasks',
        assignments: {}
      },
      {
        workArea: 'Monitoring',
        taskDescription: 'Monitor progress',
        assignments: {}
      },
      {
        workArea: 'Closure',
        taskDescription: 'Complete project deliverables',
        assignments: {}
      }
    ]
  },
  {
    id: 'utility-solar-farm',
    name: 'Utility Scale Solar Farm CAPEX',
    description: 'Complete CAPEX development cycle for utility-scale solar farm from developer perspective',
    category: 'operations',
    matrixType: 'RAPID',
    projectInfo: {
      projectSummary: 'Utility Scale Solar Farm CAPEX Development - RAPID Matrix'
    },
    tasks: [
      {
        workArea: 'Site Identification',
        taskDescription: 'Identify and evaluate potential sites',
        assignments: {}
      },
      {
        workArea: 'Site Identification',
        taskDescription: 'Conduct preliminary site assessment',
        assignments: {}
      },
      {
        workArea: 'Site Identification',
        taskDescription: 'Secure site control agreements',
        assignments: {}
      },
      {
        workArea: 'Feasibility Study',
        taskDescription: 'Conduct solar resource assessment',
        assignments: {}
      },
      {
        workArea: 'Feasibility Study',
        taskDescription: 'Perform grid interconnection study',
        assignments: {}
      },
      {
        workArea: 'Feasibility Study',
        taskDescription: 'Environmental impact assessment',
        assignments: {}
      },
      {
        workArea: 'Feasibility Study',
        taskDescription: 'Financial modeling and LCOE analysis',
        assignments: {}
      },
      {
        workArea: 'Permitting & Approvals',
        taskDescription: 'Obtain environmental permits',
        assignments: {}
      },
      {
        workArea: 'Permitting & Approvals',
        taskDescription: 'Secure building and construction permits',
        assignments: {}
      },
      {
        workArea: 'Permitting & Approvals',
        taskDescription: 'Complete interconnection agreement',
        assignments: {}
      },
      {
        workArea: 'Permitting & Approvals',
        taskDescription: 'Obtain power purchase agreement (PPA)',
        assignments: {}
      },
      {
        workArea: 'Engineering Design',
        taskDescription: 'Detailed engineering design',
        assignments: {}
      },
      {
        workArea: 'Engineering Design',
        taskDescription: 'Equipment selection and specification',
        assignments: {}
      },
      {
        workArea: 'Engineering Design',
        taskDescription: 'Civil and electrical design',
        assignments: {}
      },
      {
        workArea: 'Engineering Design',
        taskDescription: 'Grid interconnection design',
        assignments: {}
      },
      {
        workArea: 'Procurement',
        taskDescription: 'Solar panel procurement',
        assignments: {}
      },
      {
        workArea: 'Procurement',
        taskDescription: 'Inverter and electrical equipment procurement',
        assignments: {}
      },
      {
        workArea: 'Procurement',
        taskDescription: 'Balance of system procurement',
        assignments: {}
      },
      {
        workArea: 'Procurement',
        taskDescription: 'EPC contractor selection',
        assignments: {}
      },
      {
        workArea: 'Project Finance',
        taskDescription: 'Secure debt financing',
        assignments: {}
      },
      {
        workArea: 'Project Finance',
        taskDescription: 'Arrange equity investment',
        assignments: {}
      },
      {
        workArea: 'Project Finance',
        taskDescription: 'Finalize financial close',
        assignments: {}
      },
      {
        workArea: 'Construction',
        taskDescription: 'Site preparation and civil works',
        assignments: {}
      },
      {
        workArea: 'Construction',
        taskDescription: 'Installation of solar panels and racking',
        assignments: {}
      },
      {
        workArea: 'Construction',
        taskDescription: 'Electrical installation and grid connection',
        assignments: {}
      },
      {
        workArea: 'Construction',
        taskDescription: 'Quality control and testing',
        assignments: {}
      },
      {
        workArea: 'Commissioning',
        taskDescription: 'System commissioning and testing',
        assignments: {}
      },
      {
        workArea: 'Commissioning',
        taskDescription: 'Performance testing and acceptance',
        assignments: {}
      },
      {
        workArea: 'Commissioning',
        taskDescription: 'Grid synchronization and energization',
        assignments: {}
      },
      {
        workArea: 'Project Completion',
        taskDescription: 'Commercial operation date (COD)',
        assignments: {}
      },
      {
        workArea: 'Project Completion',
        taskDescription: 'Final documentation and handover',
        assignments: {}
      },
      {
        workArea: 'Project Completion',
        taskDescription: 'O&M contract activation',
        assignments: {}
      }
    ]
  },
  // RASCI Templates
  {
    id: 'rasci-project-implementation',
    name: 'Project Implementation Plan',
    description: 'Template for implementing projects with clear task responsibilities',
    category: 'operations',
    matrixType: 'RASCI',
    projectInfo: {
      projectSummary: 'Project Implementation - RASCI Matrix'
    },
    tasks: [
      {
        workArea: 'Planning',
        taskDescription: 'Develop project charter',
        assignments: {}
      },
      {
        workArea: 'Planning',
        taskDescription: 'Create work breakdown structure',
        assignments: {}
      },
      {
        workArea: 'Resources',
        taskDescription: 'Allocate team members',
        assignments: {}
      },
      {
        workArea: 'Resources',
        taskDescription: 'Procure necessary tools and equipment',
        assignments: {}
      },
      {
        workArea: 'Execution',
        taskDescription: 'Execute phase 1 deliverables',
        assignments: {}
      },
      {
        workArea: 'Execution',
        taskDescription: 'Execute phase 2 deliverables',
        assignments: {}
      },
      {
        workArea: 'Quality',
        taskDescription: 'Perform quality checks',
        assignments: {}
      },
      {
        workArea: 'Communication',
        taskDescription: 'Report progress to stakeholders',
        assignments: {}
      },
      {
        workArea: 'Closure',
        taskDescription: 'Document lessons learned',
        assignments: {}
      }
    ]
  },
  {
    id: 'rasci-product-development',
    name: 'Product Development Lifecycle',
    description: 'Template for product development with responsibility assignments',
    category: 'software',
    matrixType: 'RASCI',
    projectInfo: {
      projectSummary: 'Product Development - RASCI Matrix'
    },
    tasks: [
      {
        workArea: 'Research',
        taskDescription: 'Conduct market research',
        assignments: {}
      },
      {
        workArea: 'Research',
        taskDescription: 'Analyze competitor products',
        assignments: {}
      },
      {
        workArea: 'Design',
        taskDescription: 'Create product mockups',
        assignments: {}
      },
      {
        workArea: 'Design',
        taskDescription: 'Develop technical specifications',
        assignments: {}
      },
      {
        workArea: 'Development',
        taskDescription: 'Build prototype',
        assignments: {}
      },
      {
        workArea: 'Development',
        taskDescription: 'Develop production version',
        assignments: {}
      },
      {
        workArea: 'Testing',
        taskDescription: 'Conduct user acceptance testing',
        assignments: {}
      },
      {
        workArea: 'Testing',
        taskDescription: 'Perform integration testing',
        assignments: {}
      },
      {
        workArea: 'Launch',
        taskDescription: 'Prepare marketing materials',
        assignments: {}
      },
      {
        workArea: 'Launch',
        taskDescription: 'Execute go-to-market strategy',
        assignments: {}
      }
    ]
  },
  {
    id: 'rasci-change-management',
    name: 'Change Management Initiative',
    description: 'Template for organizational change management',
    category: 'strategy',
    matrixType: 'RASCI',
    projectInfo: {
      projectSummary: 'Change Management - RASCI Matrix'
    },
    tasks: [
      {
        workArea: 'Assessment',
        taskDescription: 'Assess current state',
        assignments: {}
      },
      {
        workArea: 'Assessment',
        taskDescription: 'Define future state vision',
        assignments: {}
      },
      {
        workArea: 'Planning',
        taskDescription: 'Develop change strategy',
        assignments: {}
      },
      {
        workArea: 'Communication',
        taskDescription: 'Create communication plan',
        assignments: {}
      },
      {
        workArea: 'Communication',
        taskDescription: 'Conduct stakeholder meetings',
        assignments: {}
      },
      {
        workArea: 'Training',
        taskDescription: 'Develop training materials',
        assignments: {}
      },
      {
        workArea: 'Training',
        taskDescription: 'Conduct training sessions',
        assignments: {}
      },
      {
        workArea: 'Implementation',
        taskDescription: 'Roll out changes',
        assignments: {}
      },
      {
        workArea: 'Monitoring',
        taskDescription: 'Track adoption metrics',
        assignments: {}
      },
      {
        workArea: 'Monitoring',
        taskDescription: 'Gather feedback and adjust',
        assignments: {}
      }
    ]
  },
  {
    id: 'rasci-service-delivery',
    name: 'Service Delivery Process',
    description: 'Template for service delivery operations',
    category: 'operations',
    matrixType: 'RASCI',
    projectInfo: {
      projectSummary: 'Service Delivery - RASCI Matrix'
    },
    tasks: [
      {
        workArea: 'Client Onboarding',
        taskDescription: 'Gather client requirements',
        assignments: {}
      },
      {
        workArea: 'Client Onboarding',
        taskDescription: 'Set up client account',
        assignments: {}
      },
      {
        workArea: 'Service Setup',
        taskDescription: 'Configure service parameters',
        assignments: {}
      },
      {
        workArea: 'Service Setup',
        taskDescription: 'Establish SLAs',
        assignments: {}
      },
      {
        workArea: 'Service Delivery',
        taskDescription: 'Execute daily operations',
        assignments: {}
      },
      {
        workArea: 'Service Delivery',
        taskDescription: 'Monitor service quality',
        assignments: {}
      },
      {
        workArea: 'Support',
        taskDescription: 'Handle customer inquiries',
        assignments: {}
      },
      {
        workArea: 'Support',
        taskDescription: 'Resolve escalated issues',
        assignments: {}
      },
      {
        workArea: 'Reporting',
        taskDescription: 'Generate performance reports',
        assignments: {}
      },
      {
        workArea: 'Improvement',
        taskDescription: 'Implement service improvements',
        assignments: {}
      }
    ]
  },
  {
    id: 'rasci-general',
    name: 'General RASCI Project',
    description: 'Basic RASCI template for task responsibility assignment',
    category: 'general',
    matrixType: 'RASCI',
    projectInfo: {
      projectSummary: 'General Project - RASCI Matrix'
    },
    tasks: [
      {
        workArea: 'Initiation',
        taskDescription: 'Define project objectives',
        assignments: {}
      },
      {
        workArea: 'Planning',
        taskDescription: 'Create project plan',
        assignments: {}
      },
      {
        workArea: 'Execution',
        taskDescription: 'Execute project tasks',
        assignments: {}
      },
      {
        workArea: 'Monitoring',
        taskDescription: 'Track progress',
        assignments: {}
      },
      {
        workArea: 'Closure',
        taskDescription: 'Complete project deliverables',
        assignments: {}
      }
    ]
  }
];

export const getCategoryColor = (category: ProjectTemplate['category']): string => {
  const colors = {
    software: '#3498db',
    marketing: '#e74c3c',
    operations: '#f39c12',
    strategy: '#9b59b6',
    general: '#95a5a6'
  };
  return colors[category];
};

export const getCategoryIcon = (category: ProjectTemplate['category']): string => {
  const icons = {
    software: '💻',
    marketing: '📢',
    operations: '⚙️',
    strategy: '🎯',
    general: '📋'
  };
  return icons[category];
};